import { Stack, SxProps, Theme, Typography } from '@mui/material';
import useCashDiscount from '../../hooks/payments/useCashDiscount';
import { formatDollarAmount } from '../../utils/global/helper';

type CashDiscountPrice =
  | {
      type: 'FIXED';
      amount: number | undefined;
    }
  | {
      type: 'HOURLY';
      hourlyPrice: number | undefined;
    }
  | {
      type: 'RANGE';
      minPrice: number | undefined;
      maxPrice: number | undefined;
    };

export const toCashDiscountPrice = (values: {
  priceType?: string;
  price?: number;
  hourlyPrice?: number;
  minPrice?: number;
  maxPrice?: number;
}): CashDiscountPrice =>
  values.priceType === 'FIXED'
    ? { type: 'FIXED', amount: values.price }
    : values.priceType === 'HOURLY'
    ? { type: 'HOURLY', hourlyPrice: values.hourlyPrice }
    : { type: 'RANGE', minPrice: values.minPrice, maxPrice: values.maxPrice };

interface Props {
  price: CashDiscountPrice;
  hideIfZero?: boolean;
  /**
   * 'default' = label-on-left layout (AcceptRequestFlow / AppointmentDetails).
   * 'attached' = full-width row that visually attaches to the input above it.
   */
  variant?: 'default' | 'attached';
  /** Existing deposit (dollars) — splits the fee into deposit + remaining portions. */
  depositPrice?: number;
  /** Selected payment method — TERMINAL uses in-store rate + min fee. */
  paymentMethod?: string | null;
  /** How the existing deposit was paid — drives rate/min fee for its portion. */
  depositPaymentMethod?: string | null;
  /** Deposit's bundled fee (cents) from backend — overrides recalc. */
  depositBundledFeeCents?: number;
  /** Quantity of the line being edited. Defaults to 1. */
  quantity?: number;
  /** Sum of (price × qty) for OTHER cart lines — drives the proportional fee split. */
  groupOtherLinesTotal?: number;
}

const formatPrices = (vals: number[]) =>
  vals.map(v => formatDollarAmount(v, true)).join(' - ');

function CashDiscountFormRow({
  price,
  hideIfZero = true,
  variant = 'default',
  depositPrice,
  paymentMethod,
  depositPaymentMethod,
  depositBundledFeeCents,
  quantity = 1,
  groupOtherLinesTotal,
}: Props) {
  const { shouldRenderCashDiscount, getAllInPrice, getCashDiscountPrice } =
    useCashDiscount();

  const rawPrices =
    price.type === 'FIXED'
      ? [price.amount ?? 0]
      : price.type === 'HOURLY'
      ? [price.hourlyPrice ?? 0]
      : [price.minPrice ?? 0, price.maxPrice ?? 0];

  const pricesSum = rawPrices.reduce((acc, curr) => acc + curr, 0);

  if (!shouldRenderCashDiscount || (hideIfZero && pricesSum <= 0)) return null;

  const prices = rawPrices.map(p => getCashDiscountPrice(p));
  const pricesWithFees = prices.map(p =>
    getAllInPrice(p, {
      depositPrice,
      paymentMethod,
      depositPaymentMethod,
      depositBundledFeeCents,
      quantity,
      groupOtherLinesTotal,
    }),
  );

  const priceValue = formatPrices(pricesWithFees);
  const cashDiscountValue = formatPrices(prices);
  const suffix = price.type === 'HOURLY' ? '/hr' : quantity > 1 ? ' each' : '';

  const sx: SxProps<Theme> =
    variant === 'attached'
      ? {
          px: 2,
          py: 1.5,
          mt: -2,
          width: '100%',
          borderRadius: '0 0 10px 10px',
          border: (theme: Theme) =>
            `1px solid ${theme.palette.outlineGrey.main}`,
          borderTop: 0,
          bgcolor: 'lightGrey.main',
          gap: 0.5,
        }
      : {
          px: 3,
          py: 1.5,
          pl: { md: '252px' },
          mb: { xs: 2.5, md: 0 },
          m: 0,
          ml: '-1px',
          width: 'calc(100% + 1px)',
          border: (theme: Theme) => `1px solid ${theme.palette.lightGrey.main}`,
          bgcolor: 'lightGrey.main',
          gap: 0.5,
        };

  return (
    <Stack boxSizing="border-box" className="input-row" sx={sx}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        width="100%">
        <Typography variant="caption" fontWeight={600}>
          Price
        </Typography>
        <Typography variant="h5" color="primary">
          {priceValue}
          {suffix}
        </Typography>
      </Stack>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        width="100%">
        <Typography variant="caption">Cash Discount</Typography>
        <Typography variant="h5">
          {cashDiscountValue}
          {suffix}
        </Typography>
      </Stack>
    </Stack>
  );
}

export default CashDiscountFormRow;
